import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { injectQueryParams } from 'ngxtension/inject-query-params';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { OAUTH2_PATH } from '@app/shared/constants/api-route.config';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { RestService } from '@app/shared/services/rest.service';
import { ThemeService } from '@app/shared/services/theme.service';

@Component({
    selector: 'cor-oauth-consent',
    templateUrl: './oauth-consent.component.html',
    imports: [ButtonModule, TooltipModule],
})
export class OAuthConsentComponent {
    protected readonly appConfig = inject(AppConfigService).appConfig;
    protected readonly themeService = inject(ThemeService);
    protected readonly currentUser = inject(AuthenticationService).currentUser;
    private readonly route = inject(ActivatedRoute);

    protected readonly clientId = injectQueryParams('client_id', {
        defaultValue: 'this application',
    });
    protected readonly scopes = injectQueryParams<string[]>('scope', {
        defaultValue: [],
        parse: value => value?.split(' ') || [],
    });
    protected readonly isAuthorizing = signal(false);

    protected api = inject(RestService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly clientName = injectQueryParams('client_name', {
        defaultValue: 'this application',
    });
    protected readonly authorizationError = signal<{
        code: string;
        description: string;
    } | null>(null);

    protected onUserConsent() {
        this.authorizationError.set(null);
        this.isAuthorizing.set(true);
        this.api
            .post<{ location: string }>(`${OAUTH2_PATH}authorize`, {}, false, {
                params: this.route.snapshot.queryParams,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ location }) => {
                    window.location.assign(location);
                },
                error: error => {
                    this.authorizationError.set({
                        code: error?.error?.error || 'authorization_failed',
                        description:
                            error?.error?.error_description ||
                            'The application could not complete the authorization request.',
                    });
                },
            })
            .add(() => this.isAuthorizing.set(false));
    }
}
